import { GetProperty } from './object.js';

const canShowRectAngleList = [0, 90, 180, 270, 360];

/**
 * 从四角坐标对象生成 points 数组
 * @param {Object} coord - 坐标对象，包含 LeftTop, RightTop, RightBottom, LeftBottom
 * @returns {Array<number>} 8个元素的数组 [x1,y1, x2,y2, x3,y3, x4,y4]
 */
export function getPointsFromCoord(coord) {
  if (!coord) return [0, 0, 0, 0, 0, 0, 0, 0];
  return [
    coord?.LeftTop?.X || 0, coord?.LeftTop?.Y || 0,
    coord?.RightTop?.X || 0, coord?.RightTop?.Y || 0,
    coord?.RightBottom?.X || 0, coord?.RightBottom?.Y || 0,
    coord?.LeftBottom?.X || 0, coord?.LeftBottom?.Y || 0
  ];
}

/**
 * 从 X,Y,Width,Height 生成 points 数组
 * @param {Object} rect - 矩形对象，包含 X, Y, Width, Height
 * @returns {Array<number>} 8个元素的数组
 */
export function getPointsFromXYWH(rect) {
  if (!rect) return [0, 0, 0, 0, 0, 0, 0, 0];
  const x = rect?.X || 0;
  const y = rect?.Y || 0;
  const w = rect?.Width || 0;
  const h = rect?.Height || 0;
  return [x, y, x + w, y, x + w, y + h, x, y + h];
}

/**
 * 从索引数组生成 points 数组
 * @param {Array} arr - 4个元素的坐标数组，每个元素包含 X, Y
 * @returns {Array<number>} 8个元素的数组
 */
export function getPointsFromArray(arr) {
  if (!arr || arr.length < 4) return [0, 0, 0, 0, 0, 0, 0, 0];
  return [
    arr[0]?.X || 0, arr[0]?.Y || 0,
    arr[1]?.X || 0, arr[1]?.Y || 0,
    arr[2]?.X || 0, arr[2]?.Y || 0,
    arr[3]?.X || 0, arr[3]?.Y || 0
  ];
}

/**
 * 根据 positionType 获取 points 数组
 * @param {string} positionType - 坐标类型 '1'|'2'|'3'
 * @param {Object|Array} coord - 坐标对象或数组
 * @returns {Array<number>} 8个元素的数组
 */
export function getPointsByPositionType(positionType, coord) {
  switch (positionType) {
    case '1':
      return getPointsFromXYWH(coord);
    case '2':
      return getPointsFromCoord(coord);
    case '3':
      return getPointsFromArray(coord);
    default:
      return [0, 0, 0, 0, 0, 0, 0, 0];
  }
}

/**
 * 自定义取整函数
 * @param {number} num - 数字
 * @returns {number} 取整后的数字
 */
export function customRound(num) {
  const number = Number(num);
  if (isNaN(number)) {
    return 0;
  }
  if (number > 0) {
    return Math.ceil(number);
  } else if (number < 0) {
    return Math.floor(number);
  } else {
    return number;
  }
}

/**
 * 获取矩形坐标位置
 * @param {Object} params - 参数对象
 * @param {Object} params.data - OCR 返回数据
 * @param {Object} params.config - 配置对象
 * @param {boolean} params.isMulPage - 是否多页
 * @param {number} params.total - 总数
 * @param {number} params.PdfPageNumber - PDF 页码
 * @returns {Array} 矩形坐标数组
 */
export const getRectPosition = ({ data, config, isMulPage, total, PdfPageNumber }) => {
  if (!canShowRectAngleList.includes(customRound(data.Angle)) && config?.dontDealAngel) return [];
  if (isMulPage && config?.cantShowPdfRect) return [];
  try {
    let _rects = [];
    if (config) {
      let StructuralList = config.StructuralList;
      let RectKey = config.RectKey;
      let NameKey = config.NameKey || 'Key.AutoName';
      let RectPageKey = config.RectPageKey || 'Value.PageIndex';
      let ValKey = config.ValKey;
      if (config.positionType === 'eduAgent') {
        const getQuestionCoord = (detail, dep) => {
          detail.forEach((item, questionId) => {
            if (item.Coord) {
              _rects.push({
                visible: false,
                cantSelect: true,
                fill: 'transparent',
                activeFill: 'rgba(0, 110, 255, 0.12)',
                stroke: '#AACFFF',
                activeStoke: '#4A70FF',
                name: dep > 1 ? `子题目${questionId + 1}` : `题目${questionId + 1}`,
                key: `Question${questionId.toString()}${dep.toString()}`,
                OriginImgHeight: data.QuestionInfo[0].Height,
                OriginImgWidth: data.QuestionInfo[0].Width,
                points: getPointsFromCoord(item.Coord[0])
              });
            }
            const TypeMap = ['Question'];
            TypeMap.forEach(v => {
              if (!item[v]?.length) return;
              item[v].forEach((que) => {
                if (que.ResultList?.length > 0) {
                  getQuestionCoord(que.ResultList, dep + 1);
                }
              });
            });
          });
        };
        getQuestionCoord(data.QuestionInfo[0].ResultList, 1);
      } else if (config.positionType === 'edu') {
        // 多页支持：QuestionInfo 数组的每一项对应一张输入图（页）
        // 单题跨页/跨栏的关键字段：
        //   - ResultList[i].Coord 是数组，可能含多个框（跨栏时同页多框、跨页时可能含多页的框）
        //   - Question/Option/Answer/Parse/Figure/Table[j].PageIndex 从 1 开始，标识片段所在页
        // 单页调用（QuestionInfo 长度 === 1）时保持原有 key 格式，避免破坏同步接口联动
        const questionInfoList = Array.isArray(data.QuestionInfo)
          ? data.QuestionInfo
          : [];
        const isMultiPage = questionInfoList.length > 1;

        // 用 PageIndex 查图片尺寸（跨页时不同页宽高可能不同）
        const getPageDim = (pageIdxOneBased) => {
          const info = questionInfoList[pageIdxOneBased - 1];
          return {
            height: info?.Height,
            width: info?.Width,
          };
        };

        questionInfoList.forEach((pageInfo, pageIdx) => {
          if (!pageInfo?.ResultList?.length) return;
          const _basePage = pageIdx + 1;
          const _height = pageInfo.Height;
          const _width = pageInfo.Width;

          // 单页保持旧 key（Question${pId}${questionId}${dep}）；多页加页前缀（Question_p${page}_...）
          const buildQuestionKey = (pId, questionId, dep, coordIdx) =>
            isMultiPage
              ? `Question_p${_basePage}_${pId}${questionId}${dep}_c${coordIdx}`
              : `Question${pId}${questionId.toString()}${dep.toString()}`;
          const buildResultKey = (pId, questionId, v, quePage, index, dep) =>
            isMultiPage
              ? `result_p${quePage}_${pId}${questionId}${v}${index}${dep}`
              : `result${pId}${questionId}${v}${index}${dep}`;

          const getQuestionCoord = (detail, dep, pId) => {
            detail.forEach((item, questionId) => {
              // 整题 Coord：可能是数组（跨栏/跨页），每个框单独生成 rect
              // 整题 rect 的 pageNum 用外层 QuestionInfo 索引（跨栏都在同页）
              const coordArr = Array.isArray(item.Coord)
                ? item.Coord
                : (item.Coord ? [item.Coord] : []);
              coordArr.forEach((coord, coordIdx) => {
                // 单页模式下沿用旧行为，只取第 1 个框
                if (!isMultiPage && coordIdx > 0) return;
                _rects.push({
                  visible: false,
                  cantSelect: true,
                  fill: 'transparent',
                  activeFill: 'rgba(0, 110, 255, 0.12)',
                  stroke: '#AACFFF',
                  activeStoke: '#4A70FF',
                  key: buildQuestionKey(pId, questionId, dep, coordIdx),
                  pageNum: _basePage,
                  OriginImgHeight: _height,
                  OriginImgWidth: _width,
                  points: getPointsFromCoord(coord)
                });
              });

              const TypeMap = ['Question', 'Option', 'Figure', 'Table', 'Answer', 'Parse'];
              TypeMap.forEach(v => {
                if (!item[v]?.length) return;
                item[v].forEach((que) => {
                  // 子片段的 pageNum 优先读 PageIndex（跨页题时不同片段属于不同页）
                  const _quePage = que.PageIndex || _basePage;
                  const dim = isMultiPage ? getPageDim(_quePage) : { height: _height, width: _width };
                  _rects.push({
                    visible: false,
                    cantSelect: true,
                    fill: 'transparent',
                    stroke: 'transparent',
                    activeFill: '#663AFA1F',
                    activeStoke: '#663AFA',
                    key: buildResultKey(pId, questionId, v, _quePage, que.Index.toString(), dep.toString()),
                    pageNum: _quePage,
                    OriginImgHeight: dim.height || _height,
                    OriginImgWidth: dim.width || _width,
                    points: getPointsFromCoord(que.Coord)
                  });
                  if (que.ResultList?.length > 0) {
                    getQuestionCoord(que.ResultList, dep + 1, questionId);
                  }
                });
              });
            });
          };
          getQuestionCoord(pageInfo.ResultList, 1, '');
        });
      } else if (config.positionType === '5') {
        let rect = [];
        Object.keys(data).map(key => {
          if (data[key] && data[key].Polygon && data[key].Polygon.length > 0) {
            rect = rect.concat(data[key].Polygon.map(i => ({
              ...i,
              key: key,
              ...data[key]
            })));
          }
        });
        _rects = rect.map(_ItemCoord => {
          return {
            visible: false,
            OriginImgHeight: _ItemCoord.OriginHeight,
            OriginImgWidth: _ItemCoord.OriginWidth,
            fill: 'transparent',
            pageNum: PdfPageNumber,
            stokeWidth: config.needHighlight ? 1 : 2,
            stroke: config.needHighlight ? '#AACFFF' : '#4A70FF',
            activeFill: config.needHighlight ? 'rgba(0, 110, 255, 0.12)' : 'transparent',
            activeStoke: '#4A70FF',
            key: `${_ItemCoord.key}-${_ItemCoord[config.riskNum]}`,
            points: getPointsFromCoord(_ItemCoord),
          };
        });
      } else if (config.positionType === '2') {
        const getRect = ({ data, curLength = 0, target = [] }) => {
          let _cur = curLength;
          let curDataIndex = StructuralList[_cur];
          if (curLength < StructuralList.length - 1) {
            for (let i = 0; i < data[curDataIndex]?.length; i++) {
              getRect({ data: data[curDataIndex][i], curLength: _cur + 1, target });
            }
          } else {
            for (let i = 0; i < data[curDataIndex]?.length; i++) {
              const _item = data[curDataIndex][i];
              const _ItemCoord = GetProperty(_item, RectKey);
              let points = getPointsByPositionType(config.positionType, _ItemCoord);
              target.push({
                visible: false,
                key: GetProperty(_item, NameKey) + GetProperty(_item, ValKey) + JSON.stringify(_ItemCoord),
                points: points,
                pageNum: GetProperty(_item, RectPageKey),
                fill: 'transparent',
                activeFill: 'rgba(0, 110, 255, 0.12)',
                stroke: '#AACFFF',
                activeStoke: '#4A70FF',
              });
            }
          }
          return target;
        };
        _rects = getRect({
          data
        });
      } else {
        const getRect = ({ data, target = [] }) => {
          for (let i = 0; i < data[config.RectInfo]?.length; i++) {
            const _item = data[config.RectInfo][i];
            const _ItemCoord = GetProperty(_item, RectKey);
            const points = getPointsByPositionType(config.positionType, _ItemCoord);
            target.push({
              visible: false,
              key: GetProperty(_item, NameKey) + GetProperty(_item, ValKey) + JSON.stringify(_ItemCoord),
              points: points,
              pageNum: GetProperty(_item, RectPageKey),
              fill: 'transparent',
              activeFill: 'rgba(0, 110, 255, 0.12)',
              stroke: '#AACFFF',
              activeStoke: '#4A70FF',
            });
          }
          return target;
        };
        _rects = getRect({
          data
        });
      }
    }
    return _rects;
  } catch (e) {
    return [];
  }
};
